/**
 * AutoDrive Theme — Cart Drawer JS
 *
 * Intercepts all add-to-cart forms, sends fetch requests to
 * /cart/add.js, re-renders the drawer, and updates the cart count.
 *
 * Features:
 *  - Async add-to-cart (no page reload)
 *  - Cart drawer open/close
 *  - Quantity updates inside drawer
 *  - Item removal
 *  - Free-shipping progress bar
 *  - Cart count badge in header
 */

(function () {
  'use strict';

  var CartDrawer = {
    drawer:    null,
    overlay:   null,
    bodyEl:    null,
    countEls:  [],
    threshold: 0,

    init: function () {
      this.drawer   = document.querySelector('[data-cart-drawer]');
      this.overlay  = document.querySelector('[data-cart-overlay]');
      this.bodyEl   = this.drawer && this.drawer.querySelector('[data-cart-drawer-body]');
      this.countEls = Array.from(document.querySelectorAll('[data-cart-count]'));

      var thresholdEl = document.querySelector('[data-free-shipping-threshold]');
      this.threshold = thresholdEl ? parseFloat(thresholdEl.dataset.freeShippingThreshold) * 100 : 0;

      if (!this.drawer) return;

      this._bindGlobalAddToCart();
      this._bindClose();
      this._bindCartItemEvents();
    },

    /* ------------------------------------------------------------------
       Open / close
       ------------------------------------------------------------------ */
    open: function () {
      if (!this.drawer) return;
      this.drawer.classList.add('is-open');
      if (this.overlay) this.overlay.classList.add('is-visible');
      document.body.style.overflow = 'hidden';
      // Focus first focusable element
      var firstFocus = this.drawer.querySelector('button, [href], input');
      if (firstFocus) setTimeout(function () { firstFocus.focus(); }, 50);
    },

    close: function () {
      if (!this.drawer) return;
      this.drawer.classList.remove('is-open');
      if (this.overlay) this.overlay.classList.remove('is-visible');
      document.body.style.overflow = '';
    },

    /* ------------------------------------------------------------------
       Intercept ALL add-to-cart form submissions
       ------------------------------------------------------------------ */
    _bindGlobalAddToCart: function () {
      var self = this;

      document.addEventListener('submit', function (e) {
        var form = e.target;
        if (!form.matches('[data-add-to-cart-form], [action="/cart/add"]')) return;

        e.preventDefault();
        var btn = form.querySelector('[data-add-to-cart-btn], [name="add"], [type="submit"]');
        if (btn) {
          btn.classList.add('btn--loading');
          btn.disabled = true;
        }

        var formData = new FormData(form);
        // Ensure JSON response
        formData.set('sections', 'cart-drawer-body');
        formData.set('sections_url', window.location.pathname);

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          body: formData
        })
        .then(function (r) {
          if (!r.ok) return r.json().then(function (d) { throw d; });
          return r.json();
        })
        .then(function () {
          return self._fetchCart();
        })
        .then(function (cart) {
          self._renderCart(cart);
          self._updateCount(cart.item_count);
          self._updateFreeShipping(cart.total_price);
          self._loadUpsell(cart);
          self.open();
          window.showToast && window.showToast('Added to cart!', 'success');
        })
        .catch(function (err) {
          var msg = (err && err.description) ? err.description : 'Could not add to cart.';
          window.showToast && window.showToast(msg, 'error');
        })
        .finally(function () {
          if (btn) {
            btn.classList.remove('btn--loading');
            btn.disabled = false;
          }
        });
      });
    },

    /* ------------------------------------------------------------------
       Close button & overlay click
       ------------------------------------------------------------------ */
    _bindClose: function () {
      var self = this;
      var closeBtn = this.drawer && this.drawer.querySelector('[data-cart-drawer-close]');

      if (closeBtn) {
        closeBtn.addEventListener('click', function () { self.close(); });
      }

      if (this.overlay) {
        this.overlay.addEventListener('click', function () { self.close(); });
      }

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && self.drawer && self.drawer.classList.contains('is-open')) {
          self.close();
        }
      });
    },

    /* ------------------------------------------------------------------
       Delegate qty/remove events inside the drawer body
       ------------------------------------------------------------------ */
    _bindCartItemEvents: function () {
      var self = this;
      if (!this.drawer) return;

      this.drawer.addEventListener('click', function (e) {
        // Quantity change
        var qtyBtn = e.target.closest('[data-qty-change]');
        if (qtyBtn) {
          var key      = qtyBtn.dataset.lineItemKey;
          var delta    = parseInt(qtyBtn.dataset.qtyChange, 10);
          var inputEl  = qtyBtn.closest('.cart-item__qty') &&
                         qtyBtn.closest('.cart-item__qty').querySelector('[data-qty-input]');
          var current  = inputEl ? parseInt(inputEl.value, 10) : 1;
          var newQty   = Math.max(0, current + delta);
          self._updateLineItem(key, newQty);
          return;
        }

        // Remove item
        var removeBtn = e.target.closest('[data-remove-item]');
        if (removeBtn) {
          var key = removeBtn.dataset.removeItem;
          self._updateLineItem(key, 0);
          return;
        }
      });

      // Qty input direct change
      this.drawer.addEventListener('change', function (e) {
        var input = e.target.closest('[data-qty-input]');
        if (!input) return;
        var key   = input.dataset.lineItemKey;
        var qty   = Math.max(0, parseInt(input.value, 10) || 0);
        self._updateLineItem(key, qty);
      });
    },

    /* ------------------------------------------------------------------
       AJAX: update a line item quantity
       ------------------------------------------------------------------ */
    _updateLineItem: function (key, qty) {
      var self = this;

      fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ id: key, quantity: qty })
      })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        self._renderCart(cart);
        self._updateCount(cart.item_count);
        self._updateFreeShipping(cart.total_price);
      })
      .catch(function () {
        window.showToast && window.showToast('Could not update cart.', 'error');
      });
    },

    /* ------------------------------------------------------------------
       Fetch current cart state
       ------------------------------------------------------------------ */
    _fetchCart: function () {
      return fetch('/cart.js', {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      }).then(function (r) { return r.json(); });
    },

    /* ------------------------------------------------------------------
       Re-render the drawer body from cart JSON
       ------------------------------------------------------------------ */
    _renderCart: function (cart) {
      if (!this.bodyEl) return;

      if (cart.item_count === 0) {
        this.bodyEl.innerHTML = this._emptyHTML();
        this._updateFooter(cart);
        return;
      }

      var itemsHTML = cart.items.map(function (item) {
        return CartDrawer._itemHTML(item);
      }).join('');

      this.bodyEl.innerHTML = itemsHTML;
      this._updateFooter(cart);
    },

    /* ------------------------------------------------------------------
       Build HTML for a single cart item
       ------------------------------------------------------------------ */
    _itemHTML: function (item) {
      var imageHTML = item.image
        ? '<img src="' + item.image + '" alt="' + _esc(item.title) + '" loading="lazy" width="72" height="72">'
        : '<div style="width:72px;height:72px;background:#f0f0f0;border-radius:4px;"></div>';

      var variantHTML = (item.variant_title && item.variant_title !== 'Default Title')
        ? '<div class="cart-item__variant">' + _esc(item.variant_title) + '</div>'
        : '';

      return [
        '<div class="cart-item" data-item-key="' + item.key + '">',
          '<div class="cart-item__image">' + imageHTML + '</div>',
          '<div class="cart-item__details">',
            '<div class="cart-item__title">' + _esc(item.product_title) + '</div>',
            variantHTML,
            '<div class="cart-item__bottom">',
              '<span class="cart-item__price">' + _formatMoney(item.line_price) + '</span>',
              '<div class="cart-item__qty">',
                '<button class="cart-item__qty-btn" data-qty-change="-1" data-line-item-key="' + item.key + '" aria-label="Decrease">−</button>',
                '<input class="cart-item__qty-input" type="number" data-qty-input data-line-item-key="' + item.key + '" value="' + item.quantity + '" min="0" aria-label="Quantity">',
                '<button class="cart-item__qty-btn" data-qty-change="1" data-line-item-key="' + item.key + '" aria-label="Increase">+</button>',
              '</div>',
            '</div>',
            '<button class="cart-item__remove" data-remove-item="' + item.key + '">Remove</button>',
          '</div>',
        '</div>'
      ].join('');
    },

    _emptyHTML: function () {
      return [
        '<div class="cart-drawer__empty">',
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">',
            '<path d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"/>',
          '</svg>',
          '<p>Your cart is empty</p>',
          '<a href="/collections/all" class="btn btn-primary btn-sm">Continue Shopping</a>',
        '</div>'
      ].join('');
    },

    _updateFooter: function (cart) {
      var footer = this.drawer && this.drawer.querySelector('[data-cart-drawer-footer]');
      if (!footer) return;

      var subtotalEl = footer.querySelector('[data-cart-subtotal]');
      if (subtotalEl) subtotalEl.textContent = _formatMoney(cart.total_price);

      // Show/hide footer based on item count
      footer.style.display = cart.item_count > 0 ? '' : 'none';
    },

    /* ------------------------------------------------------------------
       Update cart count bubble(s) in header
       ------------------------------------------------------------------ */
    _updateCount: function (count) {
      this.countEls.forEach(function (el) {
        el.textContent = count;
        el.dataset.count = count;
      });
    },

    /* ------------------------------------------------------------------
       Free shipping progress bar
       ------------------------------------------------------------------ */
    _updateFreeShipping: function (totalPrice) {
      if (!this.threshold) return;

      var bar     = this.drawer && this.drawer.querySelector('[data-shipping-bar]');
      var textEl  = this.drawer && this.drawer.querySelector('[data-shipping-text]');
      var fill    = this.drawer && this.drawer.querySelector('[data-shipping-fill]');

      if (!bar) return;

      var pct = Math.min(100, (totalPrice / this.threshold) * 100);
      if (fill) fill.style.width = pct + '%';

      if (textEl) {
        if (totalPrice >= this.threshold) {
          textEl.innerHTML = "<strong>You've unlocked free shipping! 🎉</strong>";
        } else {
          var remaining = _formatMoney(this.threshold - totalPrice);
          textEl.innerHTML = 'Add <strong>' + remaining + '</strong> more for free shipping!';
        }
      }
    },

    /* ------------------------------------------------------------------
       Upsell — load related products into cart drawer
       ------------------------------------------------------------------ */
    _loadUpsell: function (cart) {
      var upsellEl = document.querySelector('[data-cart-upsell]');
      var track    = document.querySelector('[data-upsell-track]');
      if (!upsellEl || !track || cart.item_count === 0) return;

      var productId = cart.items[0].product_id;
      fetch('/recommendations/products.json?product_id=' + productId + '&limit=4&intent=related')
        .then(function (r) { return r.json(); })
        .then(function (data) {
          // Filter out products already in cart
          var cartIds = cart.items.map(function (i) { return i.product_id; });
          var products = (data.products || []).filter(function (p) {
            return cartIds.indexOf(p.id) === -1;
          }).slice(0, 3);

          if (!products.length) return;

          track.innerHTML = products.map(function (p) {
            var imgSrc = p.featured_image
              ? p.featured_image + '&width=220'
              : '';
            var price = _formatMoney(p.price);
            return '<a href="' + _esc(p.url) + '" class="cart-upsell-card">' +
              (imgSrc ? '<img class="cart-upsell-card__img" src="' + imgSrc + '" alt="' + _esc(p.title) + '" loading="lazy">' : '') +
              '<div class="cart-upsell-card__body">' +
              '<div class="cart-upsell-card__title">' + _esc(p.title) + '</div>' +
              '<div class="cart-upsell-card__price">' + price + '</div>' +
              '</div></a>';
          }).join('');

          upsellEl.style.display = '';
        })
        .catch(function () {}); // fail silently — upsell is non-critical
    }
  };

  /* ============================================================
     HELPERS
     ============================================================ */
  function _formatMoney(cents) {
    if (!window.Shopify || !window.Shopify.currency) {
      return '$' + (cents / 100).toFixed(2);
    }
    // Simple formatter — extend with Shopify.formatMoney if money_format is available
    return (window.Shopify.currency.active || 'USD') === 'USD'
      ? '$' + (cents / 100).toFixed(2)
      : (cents / 100).toFixed(2) + ' ' + window.Shopify.currency.active;
  }

  function _esc(str) {
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str || ''));
    return div.innerHTML;
  }

  /* ============================================================
     INIT
     ============================================================ */
  document.addEventListener('DOMContentLoaded', function () {
    CartDrawer.init();
    // Expose globally so other scripts can open/close drawer
    window.CartDrawer = CartDrawer;
  });

})();
